import { notAcceptedEmailApi } from "@/providers/apis/not-accepted-email-form";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import PreviewPopupComponent from "../preview-popup/preview-popup.component.vue";
import ErrorPopupComponent from "../error-popup/error-popup.component.vue";
import { utils } from "@/utils/utils";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";
import { appConfigService } from "@/providers/services/app/app-config";
import moment from "moment";

/** Timestamps arrive as UTC "YYYY-MM-DD HH:mm:ss", sometimes with fractions. */
const TIME_FORMATS = [
  "YYYY-MM-DD HH:mm:ss.SSSSSS",
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DD HH:mm:ss",
  moment.ISO_8601,
];

@Component({
  name: "email-modal-popup",
  components: {
    AppLoaderComponent,
    PreviewPopupComponent,
    ErrorPopupComponent,
  },
})
export default class EmailModalPopupComponent extends Vue {
  @Prop()
  public applicationEmailData: any;

  /**
   * Which b-modal wraps this instance. Defaults to the id the application
   * pages have always used, so existing callers are unaffected; the
   * notification bell passes its own to avoid two modals sharing an id.
   */
  @Prop({ default: "email-modal" })
  public modalId!: string;

  /** Offer "Mark as Read" alongside Send. Only the bell wants it. */
  @Prop({ default: false })
  public showMarkAsRead!: boolean;

  /** Open with the thread already loaded, rather than behind Sent Emails. */
  @Prop({ default: false })
  public autoLoadHistory!: boolean;

  /**
   * Open on the thread rather than the composer. Reading a notification is
   * the common case; writing back is the deliberate one, so the form waits
   * behind Reply instead of greeting the reviewer with an empty message box.
   */
  @Prop({ default: false })
  public replyFirst!: boolean;

  /** Subject to seed a reply with, e.g. the thread the notification names. */
  @Prop({ default: "" })
  public defaultSubject!: string;

  public isComposeVisible = true;
  public expandedQuotes: number[] = [];
  /** Which messages are open, so their row stops repeating the body. */
  public openMessages: number[] = [];
  public replySent = false;
  /**
   * A reply has gone out from this popup. Sending one already tells the
   * server the thread is read, so Mark as Read has nothing left to do and
   * stops being offered - unlike `replySent`, which is only the confirmation
   * line and clears itself after a few seconds.
   */
  public hasReplied = false;

  public applicationId: any;
  public toEmail: any;
  public chips: any = [];
  public file: any = null;
  public files: File[] = [];
  public previewFile!: any;
  public messageInput = "";
  public subjectInput = "";
  public set = {
    type: true,
    default: true,
  };
  public isSending = false;
  public sentEmailHistory: any = [];
  public isLoading = true;
  public checkValid = false;
  public previewFileData: any[] = [];
  public showPreviewPopup = false;
  public imgType = ["jpg", "png", "jpeg", "tiff"];
  public S3Path = "";
  public errorMessage = "";

  public created() {
    this.S3Path = appConfigService.getS3FilesPathUrl();
    this.isComposeVisible = !this.replyFirst;
    if (this.applicationEmailData) {
      const applicantDetials = this.applicationEmailData.item;
      if (applicantDetials.applicationName === "PART ONE") {
        this.applicationEmailData.item.email =
          this.applicationEmailData.item.data.certificationPartOneBasicDetails.email;
      }
      if (applicantDetials.applicationName === "PART TWO") {
        this.applicationEmailData.item.email =
          this.applicationEmailData.item.data.certificationPartTwoApplicationForm.email;
      }
      if (applicantDetials.applicationName === "RENEWAL") {
        this.applicationEmailData.item.email =
          this.applicationEmailData.item.data.renewalApplicationInformation.email;
      }
      this.toEmail = applicantDetials.email;
      this.applicationId = applicantDetials.id;
      if (this.autoLoadHistory) {
        this.onGetEmailHistory();
      }
    }
  }

  public async onSendEmail() {
    this.checkValid = true;
    this.$validator.validate().then((validate: any) => {
      if (validate) {
        this.checkValid = false;
        this.isSending = true;
        const formdata = new FormData();
        formdata.append("applicationId", this.applicationId);
        formdata.append("email", this.toEmail);
        formdata.append("ccAddress", this.chips);
        formdata.append("subject", this.subjectInput);
        formdata.append(
          "message",
          JSON.parse(
            utils.textAreaTagsConversion(JSON.stringify(this.messageInput))
          )
        );
        for (let index = 0; index < this.files.length; index++) {
          formdata.append("files", this.files[index]);
        }
        notAcceptedEmailApi
          .sendEmailData(formdata)
          .then((data) => {
            this.isSending = false;
            this.hasReplied = true;
            this.$emit("emailSended", true);
            if (this.replyFirst) {
              // The reply belongs in the conversation the reviewer is reading,
              // so fold the composer away and pull the thread again rather
              // than closing on them.
              this.resetComposer();
              this.isComposeVisible = false;
              this.replySent = true;
              window.setTimeout(() => {
                this.replySent = false;
              }, 6000);
              // No isLoading flip: raising it empties the thread while the
              // refetch is in flight, which reads as the popup closing and
              // reopening. The existing messages stay put and the list swaps
              // in when the new one lands.
              this.onGetEmailHistory();
            }
          })
          .catch(() => {
            this.$emit("emailSended", false);
            this.isSending = false;
          });
      }
    });
  }

  public onGetEmailHistory() {
    notAcceptedEmailApi
      .getEmailHistory(this.applicationId)
      .then((result) => {
        this.sentEmailHistory = result.data.data;
        this.sentEmailHistory.map((history: any) => {
          // Keep a reader-facing stamp before receivedDate is overwritten with
          // the audit format the application pages show.
          //
          // The API sends UTC with no zone marker, so moment would read it as
          // local and date every message an offset early - 5h30m here.
          const stamp = moment.utc(history.receivedDate, TIME_FORMATS).local();
          history.threadTime = stamp.isValid()
            ? stamp.format("MMM D, h:mm A")
            : "";
          history.receivedDate = moment(history.receivedDate).format(
            "MM-DD-YYYY HH:mm:ss:SSS"
          );
        });
        this.isLoading = false;
        if (this.autoLoadHistory && this.sentEmailHistory.length) {
          // index 0 is the newest: the API returns the thread newest-first.
          this.openMessages = [0];
          // Newest first, so the reply that raised the notification is the one
          // worth opening; the rest stay folded like any mail client.
        }
        // After isLoading drops, since the list only renders once it does.
        this.scrollThreadToLatest();
      })
      .catch((e) => {
        this.isLoading = false;
        console.error(e);
      });
  }

  public emailAttachmentPreview(index: number, attachmentIdx: number) {
    this.previewFile = this.sentEmailHistory[index].attachments[attachmentIdx];
    if (this.previewFile) {
      if (
        this.imgType.includes(this.previewFile.uploadedFileName.split(".")[1])
      ) {
        const img = new Image();
        img.src = `${this.S3Path}/${this.previewFile.uploadedFileName}`;
        this.previewFile.width = img.width;
        this.previewFile.height = img.height;
      }
      this.previewFileData = [this.previewFile];
      this.showPreviewPopup = true;
    }
  }

  public handleFileSelect(evt: any) {
    const selectedFiles = Array.from(
      (evt?.target?.files || []) as ArrayLike<File>
    );
    const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];
    const invalidFiles = selectedFiles.filter((file: any) => {
      const extension = file.name.split(".").pop().toLowerCase();
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      this.errorMessage = "Please upload files in .jpg, .png, or .pdf format.";
      if (evt?.target) {
        evt.target.value = null;
      }
      return;
    }

    selectedFiles.forEach((data: File) => {
      this.files.push(data);
    });
    if (evt?.target) {
      evt.target.value = null;
    }
  }

  public closdedErrorPopup(event: any) {
    if (event) {
      this.errorMessage = "";
    }
  }

  public preview(index: number) {
    this.previewFile = this.files[index];
    if (this.previewFile) {
      if (this.previewFile.type.split("/")[0] === "application") {
        const file = new Blob([this.previewFile], {
          type: this.previewFile.type,
        });
        const fileURL = URL.createObjectURL(file);
        this.previewFileData = [
          {
            url: fileURL,
            name: this.previewFile.name,
            type: this.previewFile.type,
          },
        ];
        this.showPreviewPopup = true;
      } else {
        const fileURL = URL.createObjectURL(this.previewFile);
        const img = new Image();
        img.src = fileURL;
        this.previewFileData = [
          {
            width: img.width,
            height: img.height,
            url: fileURL,
            name: this.previewFile.name,
            type: this.previewFile.type,
          },
        ];
        this.showPreviewPopup = true;
      }
    }
  }

  public removeImage(index: number) {
    this.files.splice(index, 1);
  }

  public getType(type: string) {
    if (type.includes("pdf") || type.includes("doc") || type.includes("docx")) {
      return "application";
    } else {
      return "image";
    }
  }

  public hidePreviewPopup(event: boolean) {
    if (event === true) {
      this.showPreviewPopup = false;
      this.previewFileData = [];
    }
  }

  public close() {
    this.$bvModal.hide(this.modalId);
  }

  public onReply() {
    this.isComposeVisible = true;
    if (!this.subjectInput) {
      this.subjectInput = this.replySubject();
    }
  }

  /** "sub" becomes "Re: sub"; an already-prefixed subject is left alone. */
  public replySubject(): string {
    const subject = (this.defaultSubject || "").trim();
    if (!subject) {
      return "";
    }
    return /^re\s*:/i.test(subject) ? subject : `Re: ${subject}`;
  }

  /**
   * Split a reply from the trail it quotes, the way a mail client does: the
   * new text is what the reviewer needs, and the chain below it is history
   * they can open if they want it.
   *
   * The quote starts at the first blockquote, or failing that at the
   * "On <date> ... wrote:" attribution that introduces one.
   */
  private splitQuotedMessage(message: string): string[] {
    const body = message || "";
    // The attribution introduces the blockquote and belongs with it, so cut at
    // whichever appears first rather than letting "... wrote:" trail the reply.
    const marks = [
      body.search(/<blockquote/i),
      // The sender is usually a mailto link, so the attribution spans tags;
      // anchoring on a weekday keeps the loose [\s\S] scan from over-matching.
      body.search(
        /on\s+(mon|tue|wed|thu|fri|sat|sun)[a-z]*[,\s][\s\S]{0,300}?wrote\s*:/i
      ),
    ].filter((index: number) => index > -1);
    if (!marks.length) {
      return [body, ""];
    }
    const cut = Math.min(...marks);
    return [body.slice(0, cut), body.slice(cut)];
  }

  public latestMessage(sentEmail: any): string {
    return this.splitQuotedMessage(sentEmail.message)[0];
  }

  public quotedMessage(sentEmail: any): string {
    return this.splitQuotedMessage(sentEmail.message)[1];
  }

  /** "Kanimozhi S", falling back to the address when no name came through. */
  public senderName(sentEmail: any): string {
    const name = `${sentEmail.sendorFirstName || ""} ${
      sentEmail.sendorLastName || ""
    }`
      .replace(/\s+/g, " ")
      .trim();
    return name || sentEmail.sendorEmail || "Unknown sender";
  }

  /** Thumbnails that 404'd, so the row falls back instead of showing a break. */
  public failedThumbnails: string[] = [];

  /**
   * The sender's thumbnail. Every message in the thread carries it as
   * `sendorThumbnail`, so nothing extra is fetched. Empty when the sender has
   * no account, or an account with no thumbnail, which is the fallback's case.
   */
  public senderThumbnail(sentEmail: any): string {
    const thumbnail = sentEmail && sentEmail.sendorThumbnail;
    if (!thumbnail) {
      return "";
    }
    const src = `${this.S3Path}/${thumbnail}`;
    return this.failedThumbnails.includes(src) ? "" : src;
  }

  /** A stored path that no longer resolves is treated as no thumbnail at all. */
  public onThumbnailError(sentEmail: any) {
    const thumbnail = sentEmail && sentEmail.sendorThumbnail;
    if (!thumbnail) {
      return;
    }
    const src = `${this.S3Path}/${thumbnail}`;
    if (!this.failedThumbnails.includes(src)) {
      this.failedThumbnails = [...this.failedThumbnails, src];
    }
  }

  /**
   * The thread oldest-first, each entry carrying the index it has in
   * sentEmailHistory so collapse ids and the attachment preview keep
   * addressing the right message.
   *
   * The order used to come from flex-direction: column-reverse, but a
   * column-reverse scroller rests at its bottom - so the popup opened part-way
   * down the last message instead of at the start of the conversation.
   */
  public get orderedHistory(): any[] {
    return this.sentEmailHistory
      .map((mail: any, index: number) => ({ mail, index }))
      .reverse();
  }

  /**
   * One line of the message for the collapsed row, so the thread can be read
   * without opening every entry. Takes the new text only - latestMessage
   * already drops the quoted chain - then strips the markup it arrives as.
   */
  /**
   * A thread opens on its newest message. The list renders oldest-first, so
   * that is the bottom of the scroller - and a reviewer opening a notification
   * wants the reply it is about, not the message that started the conversation
   * weeks ago.
   *
   * Waited on twice: once for the rows to render, once more for anything that
   * changes their height as it settles, or the scroll lands short.
   */
  public scrollThreadToLatest() {
    this.$nextTick(() => {
      const list = this.$refs.threadList as HTMLElement;
      if (!list) {
        return;
      }
      list.scrollTop = list.scrollHeight;
      window.setTimeout(() => {
        list.scrollTop = list.scrollHeight;
      }, 0);
    });
  }

  public messageSnippet(sentEmail: any): string {
    return this.latestMessage(sentEmail)
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\s+/g, " ")
      .trim();
  }

  public isMessageOpen(index: number): boolean {
    return this.openMessages.includes(index);
  }

  /**
   * Open state is held here rather than left to b-collapse. Its show/hidden
   * events arrived against the wrong message - the list is rendered in a
   * different order from the underlying array - so the row and the body could
   * disagree about whether it was open.
   */
  public toggleMessage(index: number) {
    this.openMessages = this.isMessageOpen(index)
      ? this.openMessages.filter((i: number) => i !== index)
      : [...this.openMessages, index];
  }

  public isQuoteExpanded(index: number): boolean {
    return this.expandedQuotes.includes(index);
  }

  public toggleQuote(index: number) {
    this.expandedQuotes = this.isQuoteExpanded(index)
      ? this.expandedQuotes.filter((i: number) => i !== index)
      : [...this.expandedQuotes, index];
  }

  public onCancelReply() {
    this.isComposeVisible = false;
    this.resetComposer();
  }

  private resetComposer() {
    this.messageInput = "";
    this.subjectInput = "";
    this.chips = [];
    this.file = null;
    this.files = [];
    this.errorMessage = "";
    this.checkValid = false;
    this.$validator.reset();
  }

  public onMarkAsRead() {
    this.$emit("markAsRead", true);
    this.close();
  }
}
